import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import Breadcrumbs from '../../../../../components/breadcrumbs/Breadcrumbs';
import Button from '../../../../../components/button/Button';
import { BUTTON_TYPE } from '../../../../../components/button/constants';
import Loadable from '../../../../../components/loadable/Loadable';
import RichGrid from '../../../../../components/richgrid/RichGrid';
import SelectDropdown from '../../../../../components/dropdown/selectdropdown/SelectDropdown';
import ImportDataGridView from '../../../../components/importdatagridview/ImportDataGridView';
import { addNotifications } from '../../../../toaster/ToasterPortalSlice';
import useGarbageCollector from '../../../../../../libs/garbagecollector/garbageCollectorHooks';
import { FORM_IDS, INTEGRATION_CONSTANTS, INTEGRATION_WRAPPER_LOADING_STATES, INTEGRATION_WRAPPER_LOADING_STATES_MESSAGE_MAP, SOURCE_CONSTANTS, XERO_LOADING_STATES, XERO_LOADING_STATES_MESSAGE_MAP } from '../../constants';
import { VEC_ICON_NAME } from '../../../../../components/icons/constants';
import styles from './XeroZohoFreshBooksSuiteCommonComponent.styles';

export default function XeroZohoFreshBooksSuiteCommonComponent({
    sagaActions,
    componentKey,
    instructionsComponent: InstructionsComponent,
    integrationConstants,
    sourceConstant,
    breadcrumbsItems,
}) {
    useGarbageCollector(componentKey);

    const [taxYearOptions, setTaxYearOptions] = useState([]);
    const [formOptions, setFormOptions] = useState([]);
    const [taxYearMap, setTaxYearMap] = useState({});
    const [selectedFormMap, setSelectedFormMap] = useState({});
    const [selectedTaxYear, setSelectedTaxYear] = useState({});
    const [selectedForm, setSelectedForm] = useState({});
    const [formattedTenantList, setFormattedTenantList] = useState([]);
    const { authorizationUrl, tenantList, importData, loadingState, formAndTaxYearList, isImportDataGridViewLoaded, formSettings } = useSelector(state => state[componentKey]);
    const [queryParams, setQueryParams] = useSearchParams();
    const dispatch = useDispatch();

    const handleDisconnect = (tenantId) => {
        if (tenantId) {
            dispatch(sagaActions.disconnectToService({
                removedTenant: { idForTenant: tenantId },
                tenants: tenantList
            }));
        } else {
            dispatch(addNotifications({
                heading: "Required field missing",
                message: "Please choose a tenant to proceed"
            }));
        }
    };

    const handleFreshBooksDisconnect = (tenantId) => {
        if(tenantId){
            dispatch(sagaActions.disconnectToService({
                removedTenant: { idForTenant: tenantId },
                tenants: tenantList
            }));
        }
    };

    const handleDataLoad = (tenantId) => {
        if (tenantId && taxYearMap[tenantId] && selectedFormMap[tenantId]) {
            dispatch(sagaActions.getLoadImportData({
                integrationImportRequestDto: {
                    TaxYear: taxYearMap[tenantId],
                    FormName: selectedFormMap[tenantId],
                    BusinessId: tenantId,
                    Source: sourceConstant
                }
            }));
            setSelectedForm(selectedFormMap[tenantId]);
            setSelectedTaxYear(taxYearMap[tenantId]);
        } else {
            dispatch(addNotifications({
                heading: "Required fields missing",
                message: "Please choose tax year and form type to proceed"
            }));
        }
    };

    useEffect(() => {
        if (authorizationUrl) {
            window.location.href = authorizationUrl;
        }
    }, [authorizationUrl]);

    useEffect(() => {
        if (importData) {
            dispatch(sagaActions.getFormSetting({ formId: selectedForm, year: selectedTaxYear }));
        }
    }, [importData]);

    useEffect(() => {
        dispatch(sagaActions.getFormAndTaxYearList());
    }, []);

    useEffect(() => {
        if (queryParams.has(integrationConstants.URL_CODE)) {
            const token = queryParams.get(integrationConstants.URL_CODE);
            dispatch(sagaActions.connectToService({ code: token }));
            queryParams.delete(integrationConstants.URL_CODE);
            if (queryParams.has(integrationConstants.URL_SCOPE)) {
                queryParams.delete(integrationConstants.URL_SCOPE);
            }
            setQueryParams(queryParams);
        } else if (queryParams.has(integrationConstants.URL_ERROR)) {
            queryParams.delete(integrationConstants.URL_ERROR);
            setQueryParams(queryParams);
        } else if (queryParams.has(integrationConstants.URL_ERROR_DESCRIPTION)) {
            queryParams.delete(integrationConstants.URL_ERROR_DESCRIPTION);
            setQueryParams(queryParams);
        } else {
            dispatch(sagaActions.connectToService({ code: '' }));
        }
    }, []);

    useEffect(() => {
        if (formAndTaxYearList) {
            setTaxYearOptions(formAndTaxYearList.TaxYear);
            setFormOptions(formAndTaxYearList.FormType);
        }
    }, [formAndTaxYearList]);

    const gridColumns = [
        {
            header: 'Tenant Name',
            renderLogic: (row) => (
                <div style={styles.gridRecordsMargin}>
                    <span>{row?.TenantName}</span>
                </div>
            )
        },
        {
            header: 'Tax Year',
            renderLogic: (row) => (
                <div style={styles.gridRecordsMargin}>
                    <SelectDropdown
                        label=""
                        placeHolder="Select taxYear"
                        name="taxYear"
                        value={{ label: taxYearMap[row?.TenantId] || '', value: taxYearMap[row?.TenantId] || '' }}
                        onSelectCb={(name, selectedOption) => setTaxYearMap(prevState => ({
                            ...prevState,
                            [row.TenantId]: selectedOption.value
                        }))}
                        suffixProps={{ icon: VEC_ICON_NAME.ICON_DROPDOWN_CARET }}
                        options={taxYearOptions}
                        dropdownMenuStyle={styles.dropdownWidth}
                        dropdownContainerStyles={styles.dropdownWidth}
                    />
                </div>
            )
        },
        {
            header: 'Form Type',
            renderLogic: (row) => (
                <div style={styles.gridRecordsMargin}>
                    <SelectDropdown
                        label=""
                        placeHolder="Select Form"
                        name="selectedForm"
                        value={{ label: selectedFormMap[row?.TenantId] || '', value: selectedFormMap[row?.TenantId] || '' }}
                        onSelectCb={(name, selectedOption) => setSelectedFormMap(prevState => ({
                            ...prevState,
                            [row.TenantId]: selectedOption.value
                        }))}
                        suffixProps={{ icon: VEC_ICON_NAME.ICON_DROPDOWN_CARET }}
                        options={formOptions}
                        dropdownMenuStyle={styles.dropdownWidth}
                        dropdownContainerStyles={styles.dropdownWidth}
                    />
                </div>
            )
        },
        {
            header: 'Actions',
            renderLogic: (row) => (
                <div style={styles.gridRecordsMargin}>
                    <Button
                        type={BUTTON_TYPE.PRIMARY}
                        title="DataLoad"
                        onClickCb={() => handleDataLoad(row?.TenantId)}
                    >
                        Load Data
                    </Button>
                    {sourceConstant !== SOURCE_CONSTANTS.ZOHO_SOURCE &&
                        <Button
                            type={BUTTON_TYPE.PRIMARY}
                            title="Disconnect"
                            onClickCb={() => {
                                if (sourceConstant !== SOURCE_CONSTANTS.FRESHBOOKS_SOURCE) {
                                    handleDisconnect(row?.TenantId);
                                }
                                else {
                                    handleFreshBooksDisconnect(row?.TenantId);
                                }
                            }}
                            style={styles.gridButtonGap}
                        >
                            Disconnect
                        </Button>}
                </div>
            )
        }
    ];

    useEffect(() => {
        const fetchFormattedTenantData = () => {
            if (tenantList) {
                const formattedList = tenantList.map(tenant => ({
                    TenantName: tenant.TenantName,
                    TenantId: tenant.TenantId
                }));
                setFormattedTenantList(formattedList);
            }
            else {
                setFormattedTenantList([]);
            }
        };

        fetchFormattedTenantData();
    }, [tenantList]);

    const linkToServiceButtonLabel = () => {
        switch (sourceConstant) {
            case SOURCE_CONSTANTS.XERO_SOURCE:
                return INTEGRATION_CONSTANTS.XERO_BUTTON_LABEL;
            case SOURCE_CONSTANTS.ZOHO_SOURCE:
                return INTEGRATION_CONSTANTS.ZOHO_BUTTON_LABEL;
            case SOURCE_CONSTANTS.FRESHBOOKS_SOURCE:
                return INTEGRATION_CONSTANTS.FRESHBOOKS_BUTTON_LABEL;
            default:
                return null;
        }
    }

    return (
        <>
            <Breadcrumbs items={breadcrumbsItems} />
            {(formSettings && selectedTaxYear && selectedForm && isImportDataGridViewLoaded) ? (
                <ImportDataGridView
                    isStateFilingApplicable={false}
                    filingForCorrected={false}
                    formSettings={formSettings}
                    formName={selectedForm}
                    formId={
                        (() => {
                            switch (selectedForm) {
                                case INTEGRATION_CONSTANTS.NEC_FORM:
                                    return FORM_IDS.$1099NEC;
                                case INTEGRATION_CONSTANTS.MISC_FORM:
                                    return FORM_IDS.$1099MISC;
                                default:
                                    return null;
                            }
                        })()
                    }
                    taxYear={selectedTaxYear}
                />
            ) : (
                <div style={styles.container}>
                    <div style={styles.marginBottomStyle}>
                        <InstructionsComponent />
                    </div>
                    <Loadable
                        currentState={loadingState.state}
                        loadingStates={INTEGRATION_WRAPPER_LOADING_STATES.GRID_LOADING}
                        messageMap={INTEGRATION_WRAPPER_LOADING_STATES_MESSAGE_MAP}
                        isNewLoadable={true}
                    >
                        {formattedTenantList && gridColumns && (
                            <RichGrid
                                columns={gridColumns}
                                data={formattedTenantList}
                            />
                        )}
                        <div style={styles.marginBottomStyle}>
                            <Button
                                type={BUTTON_TYPE.PRIMARY}
                                title={"Link To Service"}
                                onClickCb={() => dispatch(sagaActions.getServiceUrl())}
                            >
                                {linkToServiceButtonLabel()}
                            </Button>
                        </div>
                    </Loadable>
                </div>
            )}
        </>
    );
}
